/* eslint-disable react/prop-types */
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import React, { useState } from "react";
import { GoogleAuthButton } from "@/components/atoms/Auth/googleAuth";
import { EmailInsteadText } from "./index";
import { Controller, useForm } from "react-hook-form";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/hooks/useAuth";
import { getCookie } from "@/network/helper";
import ResetPasswordModal from "@/components/atoms/modal/resetPassword";
import { Box, Modal } from "@mui/material";
import { Loader2, X } from "lucide-react";

export default function LoginForm({ className, setCurrentTab }) {

      const [isResetPwdOpen, setIsResetPwdOpen] = useState(false);
      const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = useForm();
    const { login } = useAuth();

  const onSubmit = async (formData) => {
    const data = {
      email: formData?.email,
      password: formData?.password,
      remember: formData?.remember,
    };

    try {
      await login(data); 

      // amplitude
      const userCookie = getCookie("user");
      if (userCookie && JSON.parse(userCookie)) {
        const user = JSON.parse(userCookie);
        const userId =
          String(user?.id).length < 5 ? `${user?.id}____` : `${user?.id}`;
        window.amplitude.setUserId(userId);
      }
    } catch (err) {
      console.error("Login error:", err);
    }
  };

  return (
    <>
              <div className="flex flex-col items-center gap-1 text-center mb-8">
            <h1 className="text-[3rem] font-bold capitalize text-foreground">
              Login to your account
            </h1>
          </div>
          <div className="mb-10">
          <GoogleAuthButton isRegister={false} />
          </div>
          <div className="mb-12">
          <EmailInsteadText />
          </div>


      <form
        onSubmit={handleSubmit(onSubmit)}
        className={cn("flex flex-col gap-6", className)}
      >

        <FieldGroup>
          <Field>
            <FieldLabel className={"text-[1.4rem] text-card-foreground"} htmlFor="email">
              Email
            </FieldLabel>
            <Input
              id="email"
              type="email"
              placeholder="m@example.com"
            className="p-4 md:text-[1.6rem] h-16 rounded-xl text-card-foreground "
              {...register("email", {
                required: "Email is required",
              })}
              required
            />
            {errors.email && (
              <FieldError className=" text-[1.2rem] mt-1 dark:text-red-500">
                {errors.email.message}
              </FieldError>
            )}
          </Field>
          <Field>
            <div className="flex items-center">
              <FieldLabel className={"text-[1.4rem] text-card-foreground"} htmlFor="password">
                Password
              </FieldLabel>
              <Button
                type="button"
                variant="ghost"
                className="ml-auto text-[1.2rem] underline-offset-4 hover:underline text-card-foreground"
                 onClick={() => setIsResetPwdOpen(true)}
              >
                Forgot your password?
              </Button>
            </div>
            <Input
              id="password"
              type="password"
            className="p-4 md:text-[1.6rem] h-16 rounded-xl text-card-foreground"
              {...register("password", {
                required: "Password is required",
                minLength: {
                  value: 8,
                  message: "Password must be at least 8 characters",
                },
              })}
              required
            />
            {errors.password && (
              <FieldError className=" text-[1.2rem] mt-1 dark:text-red-500">
                {errors.password.message}
              </FieldError>
            )}
          </Field>

          <Controller
            name="remember"
            control={control}
            defaultValue={false}
            render={({ field }) => (
              <div className="flex items-center gap-3">
                <Checkbox
                  id="remember"
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  className="scale-150"
                />
                <Label htmlFor="remember" className="text-[1.4rem] text-card-foreground">
                  Remember Me
                </Label>
              </div>
            )}
          />

          <Field>
            <Button
              className="px-8 py-7 text-[1.4rem] rounded-xl uppercase flex items-center gap-4 justify-center"
              type="submit"
              disabled={isSubmitting}
            >
              Login
              {isSubmitting &&<Loader2 className="spinner-mini" style={{
                width: "2rem",
                height: "2rem"
            }} />}
            </Button>
          </Field>
          <Field>
            <FieldDescription className="text-center text-[1.4rem]">
              Don&apos;t have an account?{" "}
              <Button
                type="button"
                onClick={() => setCurrentTab("signup")}
                variant="secondary"
                className="ml-2  text-[1.4rem] hover:underline underline-offset-8 "
              >
                Sign up
              </Button>
            </FieldDescription>
          </Field>
        </FieldGroup>
      </form>

            <ResetPasswordModal
              isOpen={isResetPwdOpen}
              handleClose={() => setIsResetPwdOpen(false)}
              handleSuccess={() => setIsSuccessModalOpen(true)}
            />
            <SuccessModal
              isOpen={isSuccessModalOpen}
              handleClose={() => setIsSuccessModalOpen(false)}
            />

    </>
  );
}


function SuccessModal({ isOpen, handleClose }) {
  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 400,
    bgcolor: "background.paper",
    boxShadow: 24,
    borderRadius: "12px",
    p: 4,
  };

  return (
    <>
      <Modal
        sx={{
          "& > .MuiBackdrop-root": {
            backdropFilter: "blur(3px)", // Adjust blur strength as needed
            backgroundColor: "rgba(0, 0, 0, 0.5)", // Optional: adjust backdrop color and opacity
          },
        }}
        open={isOpen}
        onClose={handleClose}
      >
        <Box sx={style}>
          <div>
            <div className="flex justify-between items-top">
              <h2 className="fs-18  leading-[2.8rem] text-foreground normal-case">
                We have sent a password reset link to your email. Follow the
                instructions to regain access.
              </h2>
              <div 
               onClick={handleClose}
              className="cursor-pointer">
                <X className="" style={{
                  width: 16,
                  height: 16,
                }} />
              </div>
            </div>
          </div>
        </Box>
      </Modal>
    </>
  );
}
